import Axios from "axios";

// Login tokens live in localStorage so every tab (and the plain fetch calls that
// read "token" directly) sees the latest access token after a silent refresh.
const ACCESS_TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refresh_token";

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const saveTokens = ({ access_token, refresh_token }) => {
  if (access_token) localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
};

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

let refreshPromise = null;

const requestNewTokens = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token");

  try {
    // Plain Axios (not HttpService) so the 401 interceptor never loops on this call
    const { data } = await Axios.post("refresh", { refresh_token: refreshToken });
    saveTokens(data);
    return data.access_token;
  } catch (error) {
    const code = error.response?.data?.errors?.[0]?.code;
    // Another tab rotated the same refresh token a moment ago; wait briefly for
    // it to store the new pair and use that instead of logging out.
    if (code === "refresh_token_superseded") {
      for (let attempt = 0; attempt < 10; attempt += 1) {
        if (getRefreshToken() !== refreshToken) return getAccessToken();
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }
    throw error;
  }
};

/**
 * Get a fresh access token using the stored refresh token.
 * Concurrent callers in the same tab share one request, because each refresh
 * token can only be used once.
 */
export const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = requestNewTokens().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};
